import { get, toLower, includes} from 'lodash';
import classNames from 'classnames';
import React, { Component, PropTypes } from 'react';
import { Row, Col } from 'react-bootstrap';
import ComponentTitle from '../shared/ComponentTitle';
import ChevronRight from 'components/icons/ChevronRight';
import Pagination from 'components/icons/Pagination';
import Timestamp from 'components/Timestamp';
import Image from 'components/Image';
import { showWindow, getDataOfWindowOpen } from 'utils/WindowUtils';
import { TITLE_TYPE } from '../../constants/FRNBaseStyles';
import { filterItems } from 'utils/CategoryUtils';

const GRID_ITEM_HEIGHT = 280;
const HERO_ITEM_HEIGHT = 540;

const layoutType = {
  HERO: 'hero',
  GRID: 'grid'
};

const heroPlacementType = {
  TOP: 'top',
  LEFT: 'left'
};

const imageSizingType = {
  FIXED_HEIGHT: 'fixed-height',
  SIXTEEN_NINE: 'sixteen-nine'
};

const ItemsPropTypes = {
  columnGridCount: PropTypes.number.isRequired,
  textColor: PropTypes.string,
  showTimestamp: PropTypes.bool,
  showUpdatedDate: PropTypes.bool,
  padding: PropTypes.number,
  showPill: PropTypes.bool,
  videoIconPlacement: PropTypes.string,
  timestampOptions: PropTypes.shape({
    showElapsedTime: PropTypes.bool,
    displayShortDateTime: PropTypes.bool
  })
};

const CategoryGridPropTypes = {
  layout: PropTypes.string,
  showPagination: PropTypes.bool,
  rowGridCount: PropTypes.number.isRequired,
  heroPlacement: PropTypes.string,
  heroCount: PropTypes.number,
  ...ItemsPropTypes
};

class CategoryGridWrapper extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    backgroundColor: PropTypes.string,
    showReadMore: PropTypes.bool,
    categoryUrl: PropTypes.string,
    startArticleIndex: PropTypes.number,
    types: PropTypes.array,
    displaySizes: PropTypes.array,
    totalNumberOfItems: PropTypes.number,
    titleType: PropTypes.string,
    imageSizing: PropTypes.oneOf([imageSizingType.FIXED_HEIGHT, imageSizingType.SIXTEEN_NINE]),
    ...CategoryGridPropTypes
  }

  static defaultProps = {
    layout: layoutType.GRID,
    startArticleIndex: 0,
    types: ['story', 'link'],
    totalNumberOfItems: 7,
    heroPlacement: heroPlacementType.TOP,
    heroCount: 1,
    rowGridCount: 1,
    columnGridCount: 1,
    backgroundColor: '#FFFFFF',
    textColor: '#FFFFFF',
    titleColor: '#313131',
    showPagination: true,
    showTimestamp: true,
    showUpdatedDate: false,
    showReadMore: true,
    padding: 2,
    videoIconPlacement: 'center',
    titleType: TITLE_TYPE.MODULE_TITLE,
    imageSizing: imageSizingType.FIXED_HEIGHT
  }

  render() {
      const {
        FRN_rawResponses: [{
          data: {
            features: items = [],
            headline = ''
          } = {}
        } = {}] = [],
        title,
        layout,
        titleColor,
        backgroundColor,
        textColor,
        showReadMore,
        showTimestamp,
        showUpdatedDate,
        showPill,
        categoryUrl,
        totalNumberOfItems,
        startArticleIndex,
        types,
        displaySizes = [],
        showPagination,
        heroPlacement,
        heroCount,
        rowGridCount,
        columnGridCount,
        videoIconPlacement,
        timestampOptions = {},
        titleType,
        imageSizing
      } = this.props;

      const maxValueViolation = (field, value) => {
        return ` the ${field} field must be less than ${value}`;
      };
      const minValueViolation = (field, value) => {
        return ` the ${field} field must be greater than ${value}`;
      };

      const validate = (value, field, constraint) => {
        const errors = [];

        if (constraint.min !== 'undefined' && value < constraint.min) {
          errors.push(minValueViolation(field, constraint.min));
        }
        if (constraint.max !== 'undefined' && value > constraint.max) {
          errors.push(maxValueViolation(field, constraint.max));
        }

        return errors;
      };

      let validationRules = {};
      if (layout === layoutType.GRID) {
        validationRules = {
          rowGridCount: {
            min: 1
          },
          columnGridCount: {
            min: 1,
            max: 6
          },
          heroCount: {}
        };
      } else if (layout === layoutType.HERO) {
        validationRules = {
          rowGridCount: {
            min: 0
          },
          columnGridCount: {
            min: 0,
            max: heroPlacement === heroPlacementType.LEFT ? 3 : 6
          },
          heroCount: {
            min: 1,
            max: 3
          }
        };
      }

      const sixteenNineImage = (imageSizing === imageSizingType.SIXTEEN_NINE);

      const errors = [];
      errors.push(...validate(columnGridCount, 'columnGridCount', validationRules.columnGridCount));
      errors.push(...validate(rowGridCount, 'rowGridCount', validationRules.rowGridCount));
      errors.push(...validate(heroCount, 'heroCount', validationRules.heroCount));

      if (errors.length) {
        errors.unshift(`When layout is ${layout}${layout === layoutType.HERO ? ` and heroPlacement is ${heroPlacement}` : ''}`);
        throw new Error(errors);
      }

    const offset = Math.max(startArticleIndex, 0);
    const filteredItems = filterItems(items, { type: types, displaysize: displaySizes });
    const itemsToShow = filteredItems.slice(offset, totalNumberOfItems + offset);

    if (!itemsToShow.length) {
      return null;
    }

    const componentTitle = title || headline;
    const totalItemsOfPage = rowGridCount * columnGridCount;
    const titleClassNames = classNames({ 'CategoryGrid-title': showPagination });

    return (
      <div className="CategoryGrid" style={{backgroundColor}}>
        {componentTitle ?
          <Row>
            <ComponentTitle titleType={titleType} color={titleColor} title={componentTitle} classes={titleClassNames} showArrow={false}/>
            {showReadMore && categoryUrl ?
              <div className="CategoryGrid-readMore">
                <a href={categoryUrl}> <ChevronRight color="#CCC" /> </a>
              </div> : null}
          </Row> : null}
          <CategoryGrid
            items={itemsToShow}
            layout={layout}
            showPagination={showPagination}
            heroPlacement={heroPlacement}
            heroCount={heroCount}
            columnGridCount={columnGridCount}
            rowGridCount={rowGridCount}
            textColor={textColor}
            showTimestamp={showTimestamp}
            showUpdatedDate={showUpdatedDate}
            showPill={showPill}
            padding={Math.round(this.props.padding / 2)}
            totalItemsOfPage={totalItemsOfPage}
            videoIconPlacement={videoIconPlacement}
            timestampOptions={timestampOptions}
            sixteenNineImage={sixteenNineImage}
            />
      </div>
    );
  }
}

class CategoryGrid extends Component {
  static propTypes = {
    items: PropTypes.array.isRequired,
    totalItemsOfPage: PropTypes.number,
    ...CategoryGridPropTypes
  }

  state = {
    layout: this.props.layout,
    page: 0
  }

  componentDidMount() {
    const { layout, heroPlacement, sixteenNineImage } = this.props;
    if (sixteenNineImage && layout === layoutType.HERO && heroPlacement === heroPlacementType.LEFT) {
      this._updateLeftHeroCategoryGrid();

      window.addEventListener('resize', () => this._updateLeftHeroCategoryGrid());
    }
  }

  _updateLeftHeroCategoryGrid() {
    const outerWidth = this.outerGrid.getBoundingClientRect().width;
        
    this.setState({
      outerWidth
    });
  }

  _onNext(){
    const { page } = this.state;
    this.setState({
      page: page + 1,
      layout: layoutType.GRID
    });
  }

  _onBack(){
    let { page } = this.state;
    this.setState({
      page: page - 1,
      layout: this.props.layout === layoutType.HERO && page === 1 ? layoutType.HERO : layoutType.GRID
    });
  }

  _buildGridItems(_items, _columnGridCount, itemType, _height = GRID_ITEM_HEIGHT){
    const {
      textColor,
      showTimestamp,
      showUpdatedDate,
      padding,
      showPill,
      videoIconPlacement,
      timestampOptions,
      sixteenNineImage
    } = this.props;

    return _items.map((item, index) => {
      return (
        <CategoryLink item={item} key={index} >
          <Item
            item={item}
            columnGridCount={_columnGridCount}
            itemType={itemType}
            height={_height}
            textColor={textColor}
            showTimestamp={showTimestamp}
            showUpdatedDate={showUpdatedDate}
            padding={padding}
            showPill={showPill}
            videoIconPlacement={videoIconPlacement}
            timestampOptions={timestampOptions}
            sixteenNineImage={sixteenNineImage}
        />
      </CategoryLink>);
    });
  }

  render() {
    const {
      items,
      showPagination,
      heroPlacement,
      heroCount,
      rowGridCount,
      columnGridCount,
      padding,
      totalItemsOfPage
    } = this.props;

    const {
      layout,
      page,
      outerWidth
    } = this.state;

    const isHeroGrid = (this.props.layout === layoutType.HERO);

    const startIndex = page * totalItemsOfPage + (isHeroGrid && page !== 0 ? heroCount : 0);
    const lastIndex = startIndex + totalItemsOfPage + (isHeroGrid && page === 0 ? heroCount : 0);

    const pageItems = items.slice(startIndex, lastIndex);

    let canNext = true;
    let canBack = true;

    let content;
    switch (layout) {
      case layoutType.GRID:
        content = (
          <Row>{this._buildGridItems(pageItems, columnGridCount, layoutType.GRID)}</Row>
        );

        break;
      case layoutType.HERO:
        const heroItems = pageItems.slice(0, heroCount);
        const gridItems = pageItems.slice(heroCount, pageItems.length);
        switch (heroPlacement) {
          case heroPlacementType.TOP:
            content = [];

            content.push(
              <Row key={0}>{this._buildGridItems(heroItems, heroCount, layoutType.HERO, HERO_ITEM_HEIGHT)}</Row>
            );
            content.push(
              <Row key={1}>{this._buildGridItems(gridItems, columnGridCount, layoutType.GRID)}</Row>
            );

            break;
          case heroPlacementType.LEFT:
            const colProps = {
              xs: 12,
              md: 6
            };
            let height;

            if (rowGridCount === 0 || columnGridCount === 0){
              colProps['md'] = 12;
              height = HERO_ITEM_HEIGHT;
            } else {
              height = (rowGridCount * GRID_ITEM_HEIGHT + (rowGridCount - 1) * padding * 2);
              if (heroCount > 1) {
                height = height / heroCount - ((heroCount - 1) * padding * 2) / heroCount;
              }
            }

            const heroStyle = {};
            const gridStyle = {};

            // Apply for left hero layout at breakpoint md: 992 -> 
            if (outerWidth && window.innerWidth > 991) {
              heroStyle.width = (9 * rowGridCount * outerWidth + 14 * padding * columnGridCount * (rowGridCount - heroCount)) / (9 * (heroCount * columnGridCount + rowGridCount));
              gridStyle.width = outerWidth - heroStyle.width;
            }

            content = (
              <Row>
                <div ref={node => this.outerGrid = node}>
                  <Col {...colProps} style={heroStyle}>
                    <Row>{this._buildGridItems(heroItems, 1, layoutType.HERO, height)}</Row>
                  </Col>
                  <Col {...colProps} style={gridStyle}>
                    <Row>{this._buildGridItems(gridItems, columnGridCount, layoutType.GRID)}</Row>
                  </Col>
                </div>
              </Row>
            );

            break;
          default:
            console.log(`${heroPlacement} not supported.`);
            return null;
        }

        break;
      default:
        console.log(`${layout} not supported.`);
        return null;
    }

    if (page === 0) {
      canBack = false;
    }

    if (items.length <= lastIndex) {
      canNext = false;
    }

    return (
      <div>
          {showPagination ?
            <div className="CategoryGrid-pagination">
              <button onClick={() => this._onBack()} disabled={canBack ? false : true}>
                <Pagination direction="left" />
              </button>
              <button onClick={() => this._onNext()} disabled={canNext ? false : true}>
                <Pagination direction="right" />
              </button>
            </div> : null}
            {content}
      </div>
    );
  }
}

class CategoryLink extends Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    children: PropTypes.node
  }

  render() {
    const {
      item: {
        url = '',
        link = '',
        layout = '',
        images = {},
        window: {
          width = '500',
          height = '500',
          isnewwindow = false,
          showtoolbar = true,
          isresizable = true,
          showlocation = true,
          showscrollbar = true
        } = {}
      } = {}
    } = this.props;

    const iframeImage = get(images, 'graphic.filename', '');
    const urlIFrameOrUrlLoc = layout === 3 ? (url || iframeImage) : link;
    const dataOfWindowOpen = getDataOfWindowOpen(width, height, showtoolbar, showscrollbar, showlocation, isresizable);
    const isOnClick = () => { isnewwindow && showWindow(urlIFrameOrUrlLoc, '_blank', dataOfWindowOpen);};

    return (
      <a className={ isnewwindow && 'CategoryGrid-hoverCategoryGrid' } onClick={ isOnClick } href={ isnewwindow ? null : urlIFrameOrUrlLoc }>
        { this.props.children }
      </a>
    );
  }
}

class Item extends Component {
  static propTypes = {
    item: PropTypes.object.isRequired,
    height: PropTypes.number,
    itemType: PropTypes.string,
    showPill: PropTypes.bool,
    ...ItemsPropTypes
  }

  static defaultProps = {
    height: GRID_ITEM_HEIGHT,
    itemType: layoutType.GRID,
    showPill: false
  }

  render() {
    const {
      item: {
        type = '',
        headline = '',
        images: {
          abstract: {
            filename: linkImage = ''
          } = {}
        } = {},
        abstractimage: {
          filename: newsImage = '',
          lastEditedDate = ''
        } = {},
        contentClassification: pillLabel = '',
        publishedDate,
        updatedDate,
        options: {
          date: {
            published: {
              showonabstract: showPublished = true
            } = {},
            updated: {
              showonabstract: showUpdated = true
            } = {}
          } = {}
        } = {},
      } = {},
      columnGridCount,
      textColor,
      showTimestamp,
      showUpdatedDate,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {},
      padding,
      height,
      itemType,
      showPill,
      videoIconPlacement,
      sixteenNineImage
    } = this.props;

    const wrapperStyles = {
      height: sixteenNineImage ? '100%' : `${height}px`,
      objectFit: 'cover'
    };
    
    const lg = 12 / columnGridCount;

    const maxCol = (col) => {
      return Math.max(col, lg);
    };

    const isHeroGrid = (itemType === layoutType.HERO);

    const colProps = {
      lg: columnGridCount !== 5 ? lg : null,
      md: maxCol(3),
      sm: isHeroGrid ? maxCol(6) : maxCol(4),
      xs: isHeroGrid ? 12 : maxCol(6)
    };

    // /* Breakpoints: XS: 0-767, S: 768-991, M: 992-1199, L: 1200-1469, XL: 1470-  */
    // // (max-width: 768px) 100vw, (max-width: 992px) 33vw, (max-width: 1200px) 25vw, 20vw (columnGridCount: 5 isHeroGrid: false colProps: {"lg":null,"md":3,"sm":4,"xs":6})
    // // colProps is the number of columns that a single thumbnail image should take up. For example, for 3 thubmnails across, each would span 4 columns (out of 12 total).
    //   const imageBreakpoints = [
    //     { "maxWidth": 640, "widthVW": 100 },  // set each image to 100% of the viewport
    //     { "maxWidth": 768, "widthVW": Math.round((colProps.xs / 12)                * 100) }, // grid is full width. widthVW is % of viewport per img. From example above, 3 / 12 = 25% of viewport for each image
    //     { "maxWidth": 992, "widthVW": Math.round((colProps.sm / 12)                * 100 * (690 / 992)) },   // grid scales up to 690 wide, or 70% of a 992 viewport..  At 3/12 exmaple, 70 * 25% = 17.5% of viewport for each image
    //     { "maxWidth": 1200, "widthVW": Math.round((colProps.md / 12)               * 100 * (910 / 1200)) },  // grid scales up to 910 wide, same math as above
    //     { "maxWidth": 1470, "widthVW": Math.round(((colProps.lg || (12 / 5)) / 12) * 100 * (1110 / 1470)) }, // grid scales up to 1110 wide. For some reason, when there are 5 images we set colProps.lg to null, so need to add back in 12/5
    //     {                   "widthPX": Math.round(((colProps.lg || (12 / 5)) / 12)       * 1380) },          // grid is fixed at 1380 wide. same math as above.
    //   ];


    const isVideoAttached = type.toLowerCase() === 'clip';

    const imageThumbnailClassName = classNames(
      'CategoryGrid-thumbnail',
      {
        'CategoryGrid-element CategoryGrid-coverPhoto': isHeroGrid,
        'CategoryGrid-gridItem CategoryGrid-item': !isHeroGrid,
        [`VideoThumbnail VideoThumbnail-${videoIconPlacement}`]: isVideoAttached,
        'SixteenNineImage': sixteenNineImage
      }
    );

    let timestamp;

    if (showTimestamp) {
      const publishedDateTimestamp = showPublished ? publishedDate : null;
      const updatedDateTimestamp = showUpdated ? updatedDate : null;

      const timestampDate = showUpdatedDate ? updatedDateTimestamp : publishedDateTimestamp;
      timestamp = <Timestamp publishDate={timestampDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>;
    }

    return (
      <Col {...colProps} className={`CategoryGrid-itemWraper ${columnGridCount === 5 ? 'col-lg-5ths' : ''}`} style={{padding: `${padding}px`}}>
        <div className={imageThumbnailClassName} style={wrapperStyles}>
          <Image src={ type === 'link' ? linkImage : newsImage } lastEditedDate={lastEditedDate} alt={headline} height={height} imageStyles={wrapperStyles}/>
          <div className="CategoryGrid-pictureText">
            {showPill ? <h2 className="CategoryGrid-categoryHeader">{pillLabel}</h2> : null}
            <p className="CategoryGrid-headlineSmall" style={{color: textColor}}>{headline}</p>
            {/* Pending Updated and Published timestamps from ticket WEB-566 */}
            {timestamp}
          </div>
        </div>
      </Col>
    );
  }
}

export default CategoryGridWrapper;



// WEBPACK FOOTER //
// ./app/components/news/CategoryGrid.js